import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useVendorList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refAllVendorsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'no',
      sortable: true,
    },
    {
      key: 'companyName',
      label: 'vendor',
      sortable: true,
    },
    {
      key: 'providedServiceType',
      label: 'Nature of Business',
      sortable: true,
    },
    {
      key: 'approvedDate',
      label: 'approveD on',
      sortable: true,
    },
    {
      key: 'lastOrder',
      label: 'latest PO in time frame',
      sortable: true,
    },
    {
      key: 'lastOrderAmount',
      label: 'total spend in time frame',
      sortable: true,
    },
    {
      key: 'rank',
      label: 'previous ranking',
      sortable: true,
    },
    { key: 'status', sortable: true },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalVendors = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const extraFilter = ref([])
  if (Array.isArray(route.query.extraFilter)) {
    extraFilter.value = route.query.extraFilter
  } else if (route.query.extraFilter) {
    extraFilter.value = [route.query.extraFilter]
  }
  const entity = ref([])
  if (Array.isArray(route.query.entity)) {
    entity.value = route.query.entity
  } else if (route.query.entity) {
    entity.value = [route.query.entity]
  }
  const toDeletedID = ref(null)
  const emptyAfterDate = ref(false)
  const type = ref('all-vendors')
  const vendorData = ref([])
  const purchaseOrders = ref([])
  const timeFrameFilter = ref(route.query.timeFrame)
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refAllVendorsTable.value ? refAllVendorsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalVendors.value,
    }
  })

  const refetchData = () => {
    refAllVendorsTable.value.refresh()
  }

  watch([searchQuery, extraFilter, entity, timeFrameFilter], () => {
    refetchData()
  }, { deep: true })

  const fetchVendors = (ctx, callback) => {
    store
      .dispatch('app-evaluation-vendors/fetchVendors', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        extraFilter: extraFilter.value,
        entity: entity.value,
        timeFrame: timeFrameFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { vendors, total, po } = response.data
        vendorData.value = vendors
        callback(vendors)
        totalVendors.value = total
        purchaseOrders.value = po
        if (total === 0 && timeFrameFilter.value) {
          emptyAfterDate.value = true
        }
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              extraFilter: extraFilter.value,
              entity: entity.value,
              timeFrame: timeFrameFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveVendorStatusVariant = status => {
    if (status === 'approved') return 'success'
    if (status === 'draft') return 'secondary'
    if (status === 'pending') return 'warning'
    if (status === 'non approved') return 'danger'
    return 'primary'
  }

  return {
    fetchVendors,
    tableColumns,
    perPage,
    currentPage,
    totalVendors,
    purchaseOrders,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refAllVendorsTable,

    resolveVendorStatusVariant,
    refetchData,

    toDeletedID,
    vendorData,
    emptyAfterDate,

    // Extra Filters
    extraFilter,
    timeFrameFilter,
    entity,
  }
}
